import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import PropTypes from "prop-types";
import { useSnackbar } from "notistack";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/TextLayer.css";
import "react-pdf/dist/Page/AnnotationLayer.css";

import { backendGet } from "@accredo/fe-shared/services/dataService.js";
import { getDatum } from "@accredo/fe-shared/services/formatService.js";
import { Button } from "@accredo/fe-shared/theme/components";

import { downloadDocument } from "../utils/downloadDocument.js";

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

DocumentPreview.propTypes = {
  document: PropTypes.object,
  open: PropTypes.bool,
  onClose: PropTypes.func,
};

/**
 * The document preview dialog. Only application/pdf is actually rendered,
 * via react-pdf; every other mimetype shows the filename and type with just
 * the download button.
 */
export default function DocumentPreview({ document: doc, open, onClose }) {
  const { enqueueSnackbar } = useSnackbar();
  const [fileBlob, setFileBlob] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const [vertragMenuAnchor, setVertragMenuAnchor] = useState(null);
  const navigate = useNavigate();

  const isPdf = doc?.mimetype === "application/pdf";

  useEffect(() => {
    setFileBlob(null);
    setNumPages(0);
    setLoadError(false);

    if (!open || !doc || !isPdf) {
      return;
    }

    let cancelled = false;

    backendGet(doc.download_url, {
      label: doc.filename,
      enqueueSnackbar,
      options: { responseType: "blob" },
    }).then((blob) => {
      if (cancelled) {
        return;
      }
      if (blob) {
        setFileBlob(blob);
      } else {
        setLoadError(true);
      }
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, doc?.id]);

  if (!doc) {
    return null;
  }

  const vertrags = doc.vertrags || [];

  const openVertrag = (vertrag) => {
    setVertragMenuAnchor(null);
    onClose();
    navigate(`/vertraege/${vertrag.id}`);
  };

  // A document shared by several Verträge lets the user pick one.
  const handleZumVertragClick = (event) => {
    if (vertrags.length === 1) {
      openVertrag(vertrags[0]);
    } else {
      setVertragMenuAnchor(event.currentTarget);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
        }}
      >
        <Box>
          <Typography variant="h6">{doc.bezeichnung}</Typography>
          <Typography variant="body2" color="text.secondary">
            {[
              (doc.vertrags || [])
                .map((vertrag) => vertrag.vertragsnummer)
                .join(", "),
              doc.document_date &&
                `Dokumentdatum ${getDatum(doc.document_date)}`,
              doc.imported_at && `Hinzugefügt am ${getDatum(doc.imported_at)}`,
            ]
              .filter(Boolean)
              .join(" · ")}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {doc.mimetype}
          </Typography>
        </Box>
        <IconButton onClick={onClose} aria-label="Schließen">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {isPdf ? (
          loadError ? (
            <Typography color="error">
              Dokument konnte nicht geladen werden.
            </Typography>
          ) : fileBlob ? (
            <Document
              file={fileBlob}
              onLoadSuccess={({ numPages: loaded_pages }) =>
                setNumPages(loaded_pages)
              }
            >
              {Array.from(new Array(numPages), (_, index) => (
                <Page key={`page_${index + 1}`} pageNumber={index + 1} />
              ))}
            </Document>
          ) : (
            <Typography>Dokument wird geladen …</Typography>
          )
        ) : (
          <Typography>
            {doc.filename} ({doc.mimetype}) — für diesen Dateityp ist keine
            Vorschau verfügbar.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        {vertrags.length > 0 && (
          <>
            <Button
              variant="secondary"
              onClick={handleZumVertragClick}
              sx={{ mr: "auto" }}
            >
              Zum Vertrag
            </Button>
            <Menu
              anchorEl={vertragMenuAnchor}
              open={Boolean(vertragMenuAnchor)}
              onClose={() => setVertragMenuAnchor(null)}
            >
              {vertrags.map((vertrag) => (
                <MenuItem key={vertrag.id} onClick={() => openVertrag(vertrag)}>
                  {vertrag.vertragsnummer}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
        <Button variant="tertiary" onClick={onClose}>
          Schließen
        </Button>
        <Button
          variant="primary"
          onClick={() => downloadDocument(doc, { enqueueSnackbar })}
        >
          Download
        </Button>
      </DialogActions>
    </Dialog>
  );
}
